import { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from 'react-table';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Icon from '@mui/material/Icon';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftSelect from 'components/SoftSelect';
import SoftInput from 'components/SoftInput';
import SoftPagination from 'components/SoftPagination';
import SoftButton from 'components/SoftButton';
import DataTableHeadCell from 'prefabsComponents/Tables/DataTable/DataTableHeadCell';
import DataTableBodyCell from 'prefabsComponents/Tables/DataTable/DataTableBodyCell';

const STORAGE_KEY_FILTERS = "orderPageFilters";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  onPageData,
  onFiltersUpdate,
}) {
  const defaultValue = entriesPerPage?.defaultValue ? entriesPerPage.defaultValue : 15;
  const entries = entriesPerPage?.entries ? entriesPerPage.entries : [5, 10, 15, 25, 50, 75, 100];
  const columns = useMemo(() => table?.columns, [table]);
  const data = useMemo(() => table?.rows, [table]);

  // Estado para mantener los filtros aplicados, que se leen inicialmente del localStorage.
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY_FILTERS);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setAppliedFilters(parsed);
      } catch (err) {
        console.error('Error parseando filters desde localStorage', err);
      }
    }
  }, []);

  const formatFilterLabel = (key, value) => {
    if (!value) return '';
    switch (key) {
      case 'cliente':
        return `Cliente: ${value.label || value}`;
      case 'incoterms':
        return `Incoterms: ${value.value || value}`;
      case 'estado':
        return `Estado: ${value.value || value}`;
      case 'fechaDesde':
        return `Desde: ${new Date(value).toLocaleDateString()}`;
      case 'fechaHasta':
        return `Hasta: ${new Date(value).toLocaleDateString()}`;
      case 'numeroOrden':
        return `N° Orden: ${value}`;
      default:
        return `${key}: ${value}`;
    }
  };

  const handleRemoveFilter = (key) => {
    if (!appliedFilters.hasOwnProperty(key)) return;
    const newFilters = { ...appliedFilters };
    delete newFilters[key];
    if (JSON.stringify(appliedFilters) !== JSON.stringify(newFilters)) {
      localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(newFilters));
      setAppliedFilters(newFilters);
      if (typeof onFiltersUpdate === 'function') {
        onFiltersUpdate(newFilters);
      }
    }
  };

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);
  const setEntriesPerPage = ({ value }) => setPageSize(value);

  const renderPagination = () => {
    const totalPages = pageOptions.length;
    const currentPage = pageIndex;
    const maxVisible = 9;
    let startPage, endPage;
    if (totalPages <= maxVisible) {
      startPage = 0;
      endPage = totalPages - 1;
    } else {
      const half = Math.floor(maxVisible / 2);
      if (currentPage <= half) {
        startPage = 0;
        endPage = maxVisible - 1;
      } else if (currentPage + half >= totalPages) {
        startPage = totalPages - maxVisible;
        endPage = totalPages - 1;
      } else {
        startPage = currentPage - half;
        endPage = currentPage + half;
      }
    }
    const buttons = [];
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <SoftPagination
          item
          key={i}
          onClick={() => gotoPage(i)}
          active={pageIndex === i}
        >
          {i + 1}
        </SoftPagination>
      );
    }
    return buttons;
  };

  useEffect(() => {
    if (onPageData) {
      onPageData(page.map(row => row.original));
    }
  }, [page, onPageData]);

  const onSearchChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined);
  }, 100);

  const setSortedValue = column => {
    let sortedValue;
    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? 'desc' : 'asce';
    } else if (isSorted) {
      sortedValue = 'none';
    } else {
      sortedValue = false;
    }
    return sortedValue;
  };

  const entriesStart = pageIndex === 0 ? 1 : pageIndex * pageSize + 1;
  let entriesEnd;
  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer sx={{ boxShadow: 'none', overflow: 'hidden' }}>
      {(entriesPerPage || canSearch) && (
        <SoftBox display="flex" alignItems="center" gap={2} p={3}>
          {canSearch && (
            <SoftBox width="300px">
              <SoftInput
                placeholder="Buscar..."
                value={globalFilter || ''}
                onChange={({ currentTarget }) => onSearchChange(currentTarget.value)}
              />
            </SoftBox>
          )}
          {entriesPerPage && (
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftSelect
                defaultValue={{
                  value: defaultValue,
                  label: defaultValue,
                }}
                options={entries.map(entry => ({
                  value: entry,
                  label: entry,
                }))}
                onChange={setEntriesPerPage}
                sx={{ height: 'unset' }}
              />
              <SoftTypography variant="caption" color="secondary">
                documentos por página
              </SoftTypography>
            </SoftBox>
          )}
          {Object.entries(appliedFilters)
            .filter(([key, value]) => value !== null && value !== '')
            .map(([key, value]) => (
              <SoftBox
                key={key}
                display="flex"
                alignItems="center"
                px={1}
                py={0.5}
                sx={{ background: 'rgba(0,0,0,0.05)', borderRadius: '4px' }}
              >
                <SoftTypography variant="caption">
                  {formatFilterLabel(key, value)}
                </SoftTypography>
                <SoftButton
                  variant="text"
                  onClick={() => handleRemoveFilter(key)}
                  sx={{ minWidth: 'auto', p: 0, ml: 0.5 }}
                >
                  <Icon color="error" sx={{ fontSize: 16 }}>close</Icon>
                </SoftButton>
              </SoftBox>
            ))}
        </SoftBox>
      )}
      <div style={{ overflow: 'scroll' }}>
        <Table {...getTableProps()}>
          <SoftBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, key) => (
                  <DataTableHeadCell
                    key={key}
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width || 'auto'}
                    align={column.align || 'left'}
                    sorted={setSortedValue(column)}
                  >
                    {column.render('Header')}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </SoftBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              prepareRow(row);
              return (
                <TableRow key={key} {...row.getRowProps()}>
                  {row.cells.map((cell, key) => (
                    <DataTableBodyCell
                      key={key}
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align || 'left'}
                      {...cell.getCellProps()}
                    >
                      {cell.render('Cell')}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <SoftBox display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} p={3}>
        {showTotalEntries && (
          <SoftBox mb={{ xs: 3, sm: 0 }}>
            <SoftTypography variant="button" color="secondary" fontWeight="regular" textTransform="unset">
              Mostrando {entriesStart} a {entriesEnd} de {rows.length} documentos
            </SoftTypography>
          </SoftBox>
        )}
        {pageOptions.length > 1 && (
          <SoftPagination variant={pagination?.variant || 'gradient'} color={pagination?.color || 'info'}>
            {canPreviousPage && (
              <SoftPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
              </SoftPagination>
            )}
            {renderPagination()}
            {canNextPage && (
              <SoftPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
              </SoftPagination>
            )}
          </SoftPagination>
        )}
      </SoftBox>
    </TableContainer>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 50, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: 'gradient', color: 'info' },
  isSorted: true,
  onPageData: undefined,
  noEndBorder: false,
  onFiltersUpdate: undefined,
};

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
  }).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['contained', 'gradient']),
    color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark', 'light']),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  onPageData: PropTypes.func,
  onFiltersUpdate: PropTypes.func,
};

export default DataTable;
